import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Status from 'components/field/Status';
import Accordion from 'components/global/Accordion';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import { faCircleCheck, faLoader } from '@fortawesome/pro-solid-svg-icons';

import * as S from './OrderTracking.styled';
import { OrderTable } from './Tabs/ContactDetailsTab';

const STEPS = [
	{
		status: 'searchingDriver',
		description: 'processingDescription',
		icon: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-link/processing.svg'
	},
	{
		status: 'pickedUp',
		description: 'pickedUpDescription',
		icon: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-link/pickedUp.svg'
	},
	{
		status: 'outForDelivery',
		description: 'outForDeliveryDescription',
		icon: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-link/outForDelivery.svg'
	},
	{
		status: 'delivered',
		description: 'deliveredDescription',
		icon: 'https://storage.googleapis.com/tryoto-public/public/assets/icons/OTO-link/delivered.svg'
	}
];

const OrderTracking = ({
	orderNumber,
	trackingURL,
	deliveryCompany,
	deliveryCompanyLogo,
	trackingNumber,
	orderDate,
	customerName,
	originCountry,
	originCity,
	destinationCountry,
	destinationCity,
	statusHistory,
	status
}) => {
	const { t } = useTranslation();
	const { getLocaleDate, getTimeWithAMPM } = useLocale();

	const steps = useMemo(() => {
		if (!statusHistory?.length) {
			return STEPS;
		}

		const lastCompletedStep = [...STEPS]
			.reverse()
			.find((step) => statusHistory.find((status) => status.status === step.status));

		const lastCompletedStepIndex = lastCompletedStep ? STEPS.indexOf(lastCompletedStep) : 0;

		const completedSteps = STEPS.slice(0, lastCompletedStepIndex + 1);

		return STEPS.map((step, index) => {
			return {
				...step,
				date: statusHistory.find((status) => status.status === step.status)?.createdAt || null,
				isCompleted: completedSteps?.find?.((status) => status?.status === step.status) !== undefined,
				isCurrentStep: completedSteps.length === index
			};
		});
	}, [statusHistory]);

	return (
		<S.Wrapper>
			<S.Card padding={0} background border={0} shadowed rounded>
				<S.CardTitleContainer>
					<Typography variant="h4" fontWeight="700" noWrap>
						<Trans
							i18nKey="orderInformationWithOrderNumber"
							values={{ orderNumber }}
							components={{
								light: <Typography variant="p1" className="order-number" />
							}}
						/>
					</Typography>
					<Status name="status" value={status} />
				</S.CardTitleContainer>

				<S.OrderInfoContainer>
					<TopInfoBox title="storeName" value={customerName} />
					<TopInfoBox
						title="deliveryCompany"
						value={
							<DeliveryCompany
								deliveryCompany={deliveryCompany}
								deliveryCompanyLogo={deliveryCompanyLogo}
								trackingURL={trackingURL}
								trackingNumber={trackingNumber}
							/>
						}
					/>
					<TopInfoBox title="origin" value={`${t(originCity)}, ${t(originCountry)}`} />
					<TopInfoBox title="origin" value={`${t(destinationCity)}, ${t(destinationCountry)}`} />
					<TopInfoBox
						title="orderDate"
						value={
							<Trans
								i18nKey="otoLinkDeliveryDate"
								values={{
									deliveryHour: getTimeWithAMPM(orderDate),
									deliveryDate: getLocaleDate(orderDate)
								}}
							/>
						}
					/>
				</S.OrderInfoContainer>

				<S.Line />

				<S.OrderTrackingContainer>
					{steps.map((step, index) => (
						<S.StepRow
							key={index}
							isGrayScale={!step.isCompleted && !step.isCurrentStep}
							isCurrentStep={step.isCurrentStep || (step.isCompleted && index === steps.length - 1)}>
							<Image url={step.icon} alt="order number" width="6.5rem" height="6.5rem" />
							<S.StepContent>
								<S.StepInfo>
									<div>
										<Typography variant="p2" fontWeight="700" noWrap className="pb-2">
											{step.status}
										</Typography>
										<Typography variant="p3" noWrap>
											{step.description}
										</Typography>
									</div>
									{step.date && (
										<Typography variant="caption" noWrap>
											{getLocaleDate(step.date)} - {getTimeWithAMPM(step.date)}
										</Typography>
									)}
								</S.StepInfo>
								{step.isCompleted && <Icon icon={faCircleCheck} color="var(--blue)" size="medium" />}
								{step.isCurrentStep && index !== steps.length && (
									<Icon icon={faLoader} color="var(--content-3)" size="medium" />
								)}
							</S.StepContent>
							{index + 1 !== steps.length && <S.TimeLineRow isCurrentStep={step.isCurrentStep} />}
						</S.StepRow>
					))}
				</S.OrderTrackingContainer>
				<S.Line />
				<Accordion header="orderItems" defaultVisible={true}>
					<S.AccordionInner>
						<OrderTable />
					</S.AccordionInner>
				</Accordion>
			</S.Card>
		</S.Wrapper>
	);
};

const TopInfoBox = ({ title, value }) => {
	return (
		<S.TopInfoContainer>
			<Typography variant="p2" color="var(--content-3)">
				{title}
			</Typography>
			<Typography variant="p2" fontWeight="500">
				{value}
			</Typography>
		</S.TopInfoContainer>
	);
};

const DeliveryCompany = ({ deliveryCompany, deliveryCompanyLogo, trackingURL, trackingNumber }) => {
	return (
		<S.DCContainer>
			<S.DCLogoContainer>
				<Image url={deliveryCompanyLogo} alt="logo" />
			</S.DCLogoContainer>
			<S.DCInfoContent>
				<Typography fontWeight="500" variant="p2">
					{deliveryCompany}
				</Typography>
				<a href={trackingURL} target="_blank" rel="noreferrer">
					<Typography variant="p3" noWrap fontWeight="700" color="var(--blue)">
						{trackingNumber}
					</Typography>
				</a>
			</S.DCInfoContent>
		</S.DCContainer>
	);
};

export default OrderTracking;
